import React, { useState, useEffect, useCallback } from 'react';
import SongList from './components/SongList';
import SongEditor from './components/SongEditor';
import ChordLibrary from './components/ChordLibrary';
import Login from './Login';
import ResetPasswordPage from './ResetPassword';
import Footer from './components/Footer';
import FeedbackPage from './FeedbackPage';
import TermsOfService from './TermsOfService';
import Account from './Account';
import SuccessPage from './SuccessPage';

// Use Environment Variable for API URL (defaults to localhost if not set)
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_SONGS = `${BASE_URL}/api/songs`;
const API_ME = `${BASE_URL}/api/me`;
const FREE_SONG_LIMIT = 5;

function App() {
  // --- 1. INITIALIZE HOOKS FIRST ---
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [username, setUsername] = useState(localStorage.getItem('username'));
  const [email, setEmail] = useState(localStorage.getItem('email') || '');
  const [plan, setPlan] = useState(localStorage.getItem('userPlan') || 'free');
  
  const [view, setView] = useState('home'); 
  const [songs, setSongs] = useState([]);
  const [selectedSong, setSelectedSong] = useState(null);

  // --- LOGOUT LOGIC ---
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('userPlan');
    setToken(null);
    setUsername(null);
    setEmail('');
    setPlan('free');
    setSongs([]); 
  };

  // --- LOGIN SUCCESS LOGIC ---
  const handleLoginSuccess = (newToken, newUsername, newPlan, newEmail) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('username', newUsername);
    localStorage.setItem('userPlan', newPlan);
    localStorage.setItem('email', newEmail);
    
    setToken(newToken);
    setUsername(newUsername);
    setPlan(newPlan);
    setEmail(newEmail);
    setView('home');
  };

  // --- FETCH USER DATA (To refresh plan status silently) ---
  // FIX: Wrapped in useCallback to satisfy useEffect dependency requirements
  const refreshUser = useCallback(() => {
    if (!token) return;
    fetch(API_ME, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(user => {
        if(user.plan) {
            setPlan(user.plan);
            localStorage.setItem('userPlan', user.plan);
        }
        if(user.email) {
            setEmail(user.email);
            localStorage.setItem('email', user.email);
        }
      })
      .catch(console.error);
  }, [token]);

  // --- FETCH SONGS (Protected) ---
  useEffect(() => {
    const isResetPage = window.location.pathname.startsWith('/reset-password');
    const isSuccessPage = window.location.pathname.startsWith('/success');

    if (token && view === 'home' && !isResetPage && !isSuccessPage) {
      // 1. Fetch Songs
      fetch(API_SONGS, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => {
        if (res.status === 401 || res.status === 403) {
            handleLogout(); 
            return [];
        }
        return res.json();
      })
      .then(data => setSongs(Array.isArray(data) ? data : []))
      .catch(console.error);

      // 2. Refresh User Plan
      refreshUser();
    }
  }, [view, token, refreshUser]); // FIX: Added refreshUser dependency

  // --- 2. ROUTING CHECKS ---
  const path = window.location.pathname;
  
  if (path.startsWith('/reset-password')) {
     return <ResetPasswordPage />;
  }

  if (path.startsWith('/success')) {
      return <SuccessPage onBack={() => {
          window.location.href = '/'; // FIX: Force full redirect to clear URL
      }} />;
  }

  if (!token) {
    return <Login onLogin={handleLoginSuccess} />;
  }

  // --- 3. MAIN APP UI ---
  return (
    <div className="App">
      {/* User Header */}
      <div style={{
          display:'flex', justifyContent:'flex-end', alignItems:'center', 
          padding:'10px 20px', background:'#f8f9fa', borderBottom:'1px solid #ddd'
      }}>
        {/* CLICKABLE USERNAME BUTTON */}
        <button 
            onClick={() => setView('account')}
            style={{
                marginRight:'15px', 
                background: 'transparent',
                border: 'none',
                fontWeight:'bold', 
                color:'#555',
                textTransform: 'capitalize',
                cursor: 'pointer',
                fontSize: '1rem',
                textDecoration: 'underline'
            }}
            title="Go to Account Settings"
        >
          Hello, {username}
        </button>

        <button onClick={handleLogout} className="btn-text-toggle" style={{background:'white', border:'1px solid #ccc'}}>
          Log Out
        </button>
      </div>

      {/* --- HOME VIEW (With Footer) --- */}
      {view === 'home' && (
        <div style={{display:'flex', flexDirection:'column', minHeight:'calc(100vh - 60px)'}}>
          <div style={{flex: 1}}>
            <SongList 
              songs={songs} 
              userPlan={plan}     
              onSelect={(song) => { setSelectedSong(song); setView('editor'); }} 
              onCreate={() => {
                if (plan === 'free' && songs.length >= FREE_SONG_LIMIT) return;
                setSelectedSong({
                  title: "New Song", artist: "", key: "C", isPublic: false,
                  sections: [{ title: "Verse 1", content: "", strumming: "" }]
                });
                setView('editor');
              }} 
            />
            <div style={{textAlign: 'center', marginTop: '20px', paddingBottom:'50px'}}>
               <button onClick={() => setView('chords')} style={{background:'none', border:'none', color:'#007bff', textDecoration:'underline', cursor:'pointer', fontSize:'1.1rem'}}>
                 Open Global Chord Library
               </button>
            </div>
          </div>
          <Footer onNavigate={setView} />
        </div>
      )}
      
      {/* --- ACCOUNT VIEW --- */}
      {view === 'account' && (
        <Account 
          user={{ username, email, plan }} 
          token={token} 
          onNavigate={setView} 
        />
      )}
      
      {/* --- EDITOR VIEW --- */}
      {view === 'editor' && (
        <SongEditor 
          initialSong={selectedSong} 
          token={token} 
          onBack={() => setView('home')} 
          onNavigate={setView} 
        />
      )}

      {/* --- CHORD LIBRARY --- */}
      {view === 'chords' && (
        <ChordLibrary 
          token={token} 
          userPlan={plan}
          onBack={() => setView('home')} 
        />
      )}

      {/* --- FEEDBACK PAGE --- */}
      {view === 'feedback' && (
        <FeedbackPage 
          token={token} 
          onBack={() => setView('home')} 
        />
      )}

      {/* --- TERMS OF SERVICE --- */}
      {view === 'tos' && (
        <TermsOfService 
          onBack={() => setView('home')} 
        />
      )}

    </div>
  );
}

export default App;
